// CLI v0.8.10 — `grid rename <slug> <new-display-name>` (backlog 112).
//
// Renames an entity's display_name. Works for both runtimes (apps/agents) and
// inspirations. The lookup is deterministic:
//
//   1. Try GET /api/v2/runtimes/<slug>. On success the target is a runtime;
//      we PATCH /api/v2/entities/<entity_id> with { display_name }.
//   2. On 404 RUNTIME_NOT_FOUND, try GET /api/v2/inspirations/<slug>. On
//      success the target is an inspiration; we PATCH
//      /api/v2/inspirations/<entity_id> with { display_name }.
//   3. If neither resolves, surface a single "not found" error referencing
//      the slug.
//
// Slugs (yaml `name:`) are immutable — server-side identity, decision 012.
// Renaming is a display-only operation; the URL path and namespace stay put.

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { CloudGridError } from '../utils.js';
import { handleV2Error } from '../handle-error.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';

export interface RenameOpts {
  json?: boolean;
  output?: string;
}

interface RuntimeLookupResponse {
  entity_id: string;
  name: string;
  display_name: string;
}

interface InspirationLookupResponse {
  entity_id: string;
  name: string;
  display_name: string;
}

interface RenameResponse {
  entity_id: string;
  name: string;
  display_name: string;
  kind: string;
  realm: 'runtime' | 'inspiration';
}

export async function runRename(
  client: IV2Client,
  slug: string,
  newDisplayName: string,
  opts: RenameOpts = {},
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const trimmedDisplay = newDisplayName.trim();
  if (trimmedDisplay.length < 1 || trimmedDisplay.length > 100) {
    throw new CloudGridError(
      'New display name must be 1 to 100 characters.',
    );
  }

  // 1. Try runtime lookup
  let runtime: RuntimeLookupResponse | null = null;
  try {
    runtime = await client.get<RuntimeLookupResponse>(
      `/api/v2/runtimes/${encodeURIComponent(slug)}`,
    );
  } catch (err) {
    if (!(err instanceof V2ApiError) || err.status !== 404) {
      throw err;
    }
  }

  if (runtime) {
    const result = await client.patch<RenameResponse>(
      `/api/v2/entities/${runtime.entity_id}`,
      { display_name: trimmedDisplay },
    );
    out.json(() => ({ name: result.name, display_name: result.display_name }));
    out.human(() =>
      `Renamed ${result.name} to "${result.display_name}".\n  Slug unchanged: ${result.name}`,
    );
    return;
  }

  // 2. Try inspiration lookup
  let inspiration: InspirationLookupResponse | null = null;
  try {
    inspiration = await client.get<InspirationLookupResponse>(
      `/api/v2/inspirations/${encodeURIComponent(slug)}`,
    );
  } catch (err) {
    if (!(err instanceof V2ApiError) || err.status !== 404) {
      throw err;
    }
  }

  if (inspiration) {
    const result = await client.patch<{ entity_id: string; name: string; display_name: string }>(
      `/api/v2/inspirations/${inspiration.entity_id}`,
      { display_name: trimmedDisplay },
    );
    out.json(() => ({ name: result.name, display_name: result.display_name }));
    out.human(() =>
      `Renamed ${result.name} to "${result.display_name}".\n  Slug unchanged: ${result.name}`,
    );
    return;
  }

  throw new CloudGridError(
    `No runtime or inspiration named '${slug}' in your active grid.`,
  );
}

export const renameCommand = addOutputOption(
  new Command('rename')
    .description("Rename an entity's display name (slug stays the same)")
    .argument('<slug>', 'Entity slug (yaml name)')
    .argument('<new-display-name>', 'New display name (1 to 100 characters)')
    .option('--json', 'Machine-readable JSON output (alias of -o json)'),
).action(async (slug: string, newDisplayName: string, opts: RenameOpts) => {
  try {
    const client = await getV2Client();
    await runRename(client, slug, newDisplayName, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
